import { useState } from 'react';
import { Mail, Gift, Bell, Star, CheckCircle } from 'lucide-react';
import Button from '../base/Button';

export default function NewsletterSection() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setIsSubmitting(true);
    
    try {
      // Pour un envoi réel, vous devrez configurer un endpoint backend
      // Exemple: const response = await fetch('/api/newsletter', { 
      //   method: 'POST', 
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify({ email, source: 'newsletter-homepage', preferences: 'offres-speciales,nouveautes,conseils' })
      // });
      
      // Simulation d'inscription réussie (à remplacer par un vrai endpoint)
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setIsSubmitted(true);
      setEmail('');
    } catch (error) {
      console.error('Erreur lors de l\'inscription:', error);
      alert('Une erreur est survenue lors de l\'inscription. Veuillez réessayer plus tard.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const benefits = [
    {
      icon: Gift,
      title: "Offres Exclusives",
      description: "Réductions jusqu'à -30% réservées aux abonnés"
    },
    {
      icon: Bell,
      title: "Nouveautés en Avant-Première",
      description: "Découvrez les nouvelles activités en premier"
    },
    {
      icon: Star,
      title: "Conseils d'Experts",
      description: "Tips et bons plans de notre équipe à Dubai"
    }
  ];

  if (isSubmitted) {
    return (
      <section className="py-20 bg-gradient-to-r from-green-600 to-emerald-600">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="bg-white rounded-2xl p-8 shadow-xl">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle size={32} className="text-green-600" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Merci pour votre inscription !
            </h2>
            <p className="text-gray-600 mb-6">
              Vous recevrez bientôt nos meilleures offres et conseils exclusifs dans votre boîte mail.
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-amber-800 font-medium">
                🎁 Offre de bienvenue : -15% sur votre première réservation avec le code WELCOME15
              </p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 bg-gradient-to-r from-blue-900 via-blue-800 to-blue-900 relative overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}></div>
      </div>

      <div className="max-w-6xl mx-auto px-4 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left side - Content */}
          <div className="text-white">
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">
              Restez Connecté avec <span className="text-amber-400">Dubai</span>
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              Recevez nos offres exclusives, nouveautés et conseils d'experts 
              directement dans votre boîte mail. Plus de 50 000 voyageurs nous font déjà confiance !
            </p>

            {/* Benefits */}
            <div className="space-y-4 mb-8">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-amber-400 rounded-full flex items-center justify-center flex-shrink-0">
                    <benefit.icon size={20} className="text-blue-900" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">{benefit.title}</h3>
                    <p className="text-blue-200">{benefit.description}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Trust indicators */}
            <div className="flex items-center space-x-6 text-blue-200">
              <div className="flex items-center space-x-2">
                <Mail size={16} />
                <span className="text-sm">Pas de spam</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle size={16} />
                <span className="text-sm">Désabonnement facile</span>
              </div>
              <div className="flex items-center space-x-2">
                <Star size={16} />
                <span className="text-sm">50k+ abonnés</span>
              </div>
            </div>
          </div>

          {/* Right side - Form */}
          <div className="bg-white rounded-2xl p-8 shadow-2xl">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Gift size={32} className="text-amber-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                Offre Spéciale
              </h3>
              <p className="text-gray-600">
                <strong className="text-amber-600">-15% sur votre première réservation</strong><br />
                en vous inscrivant à notre newsletter
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Votre adresse email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="votre@email.com"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-colors"
                />
              </div>

              {/* Hidden fields for form processing */}
              <input type="hidden" name="source" value="newsletter-homepage" />
              <input type="hidden" name="preferences" value="offres-speciales,nouveautes,conseils" />

              <Button 
                type="submit" 
                className="w-full" 
                size="lg"
                loading={isSubmitting}
                disabled={!email || isSubmitting}
              >
                {isSubmitting ? 'Inscription en cours...' : 'Recevoir mes offres exclusives'}
              </Button>

              <p className="text-xs text-gray-500 text-center">
                En vous inscrivant, vous acceptez de recevoir nos emails marketing. 
                Vous pouvez vous désabonner à tout moment.
              </p>
            </form>

            {/* Social proof */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="flex items-center justify-center space-x-4 text-sm text-gray-600">
                <div className="flex items-center space-x-1">
                  <div className="flex -space-x-2">
                    <div className="w-6 h-6 bg-blue-500 rounded-full border-2 border-white"></div>
                    <div className="w-6 h-6 bg-green-500 rounded-full border-2 border-white"></div>
                    <div className="w-6 h-6 bg-purple-500 rounded-full border-2 border-white"></div>
                  </div>
                  <span>+50k abonnés</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Star className="text-amber-400 fill-current" size={16} />
                  <span>4.9/5 satisfaction</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}